// src/Projects/projects.js
// Single unified project database for detailed case studies (frontend + backend + CRM + AI)
const projects = [
  {
    id: "portfolio-website",
    title: "Chahat Portfolio — Fullstack (Frontend + Backend)",
    tagline: "A dockerized developer portfolio and storytelling blogging platform with Redis caching, built end-to-end.",
    cover: "/assets/projects/portfolio/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook-2.png", alt: "Home page" }
    ],
    overview: `
      Chahat Portfolio is a production-ready, dockerized fullstack portfolio and storytelling case study engine. 
      The frontend is a component-driven React app featuring dynamic category tab filters, estimated read time calculations, scroll progress indicators, and sticky Table of Contents anchors.
      The backend is a Node.js + Express API orchestrated using Docker Compose alongside a Redis cache invalidation layer, secured via stateless JWT cookies, Zod request validations, and Winston logging profiles.
    `,
    importance: "Consolidating frontend and backend architectures showcases full-ownership SDE skills including secure session management, database index optimizations, container orchestration, and stateful caching systems.",
    problem: "Providing a secure, scalable, and maintainable platform to manage personal projects and blogs, while enabling admin resume updates and blog edits without manual deploy cycles. The system must run reliably under spikes and block payload spam on comment/lead endpoints.",
    solution: "Implemented a component-driven React frontend with lazy routes and skeleton loaders, paired with a layered Express backend exposing RESTful endpoints, protected by JWT and role-based access control (RBAC). Integrated Redis database query caching, Winston logging levels, and Zod input validators.",
    howItWorks: [
      "Visitors browse public sections (projects, blogs) and download role-specific resumes.",
      "The frontend fetches blog posts and comments from the Express API endpoints.",
      "Admin credentials authenticate via secure cookies and JWT tokens to unlock dashboard CRUD capabilities.",
      "Any write operation (create/edit/delete blog or comments) automatically triggers a Redis cache invalidation workflow using asynchronous scanning patterns."
    ],
    features: [
      "Role-specific resume selector (SDE, Salesforce, Automation)",
      "High-contrast categorized blog feeds (Case Studies, Tech Journeys, Deep Dives)",
      "Dynamic cross-links linking blog posts and project showcase pages",
      "Stateless Redis database cache layer with async invalidate scanning",
      "Fully dockerized multi-container orchestration environment",
      "Aesthetics upgrades: Reading scroll tracking bar, copy-to-clipboard code blocks, and markdown live previews",
      "Moderator dashboards allowing real-time edits, category mapping, and comments deletion"
    ],
    techStack: {
      frontend: ["ReactJS", "TailwindCSS", "Zustand", "Framer Motion", "ReactMarkdown", "remark-gfm"],
      backend: ["NodeJS", "Express", "JWT Auth", "Multer", "Redis Cache", "Zod", "Winston Logger"],
      infra: ["Docker & Docker Compose", "MongoDB Atlas", "Vercel (Frontend)", "CI/CD GitHub Actions"]
    },
    industryPractices: [
      "Lighthouse optimization goals (95+ score)",
      "Secure, cookie-based JWT session authentication",
      "Route-level code splitting and skeleton loading screens",
      "Centralized state management with Zustands to minimize component re-renders"
    ],
    testing: {
      frontend: "Warm React component tests checking navigation transitions, form boundaries, and responsive layouts.",
      backend: "Supertest assertions confirming routing status responses, Zod validation schema rejections, and JWT token authorization checks.",
      e2e: "Cypress automated runs testing login credentials gates, blog postings, and comment additions."
    },
    securityMeasures: [
      "Helmet middleware for secure HTTP response headers",
      "Route-specific rate limiters preventing auth brute-forcing and chatbot API resource drains",
      "Strict Zod schema input validations on comments, user registration, and blog posts",
      "Secure, cookie-based JWT session authentication and RBAC admin authorizations",
      "Middlewares checking ObjectID formats to trap invalid parameter injection errors"
    ],
    performanceOptimizations: [
      "Cursor-based Redis SCAN iterators invalidating cache patterns asynchronously without blocking event loops",
      "Client-side Zustand store preloading caching blog listings to minimize unnecessary backend request latency",
      "Dynamic loading of heavy editing utilities (markdown parser)",
      "Asset delivery using WebP formats and lazy loading viewport images"
    ],
    observability: {
      errors: "Sentry dashboard monitoring client/server runtime errors",
      logs: "Winston logging output aggregated into server logs"
    },
    architecture: [
      "React SPA Client",
      "Axios Interceptors",
      "Express API Router",
      "JWT Auth Middleware",
      "Redis Cache Engine",
      "Mongoose ODM Layer",
      "MongoDB Atlas Database"
    ],
    links: {
      live: "https://chahat-portfolio.vercel.app/",
      repo: "https://github.com/Chahatj23/Chahat-Portfolio"
    },
    stats: {
      stars: 45,
      users: "2K+ monthly pageviews",
      performance: "Lighthouse 97+"
    },
    responsibilities: [
      "Designed dark-mode UI mockup and responsive CSS layouts.",
      "Coded authentication controllers, Express routers, and Mongoose schemas.",
      "Configured automatic GitHub Actions deployment pipeline.",
      "Integrated Redis caching, Docker container configurations, and Zod validator middlewares."
    ],
    highlights: [
      "Docker Multi-Container orchestration for development and production scaling.",
      "Redis cached queries with automated invalidation workflows.",
      "Table of Contents anchor slug generators and next/previous article sliders."
    ],
    timeline: "Jan 2025 – Present",
    status: "Active"
  },
  {
    id: "stockpilot-intelligent-inventory-sales-management",
    title: "StockPilot — Intelligent Inventory & Sales SaaS",
    tagline: "AI-powered predictive inventory replenishment and sales promo optimizer.",
    cover: "/assets/projects/stockpilot/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "Inventory Manager View" }
    ],
    overview: `
      StockPilot is an enterprise-grade, predictive inventory forecasting and promotions orchestration SaaS.
      It integrates Google Genkit AI paired with Gemini large language models to analyze complex sales velocity trends, predict seasonal demand shifts, and recommend context-aware discount codes for slow-moving stock.
      Built with Next.js, TypeScript, and MongoDB Atlas, it implements compound indexing and transaction queues to ensure safe stock adjustments in multi-operator warehouses.
    `,
    importance: "Demonstrates advanced database schema scaling, compound lookup query optimization, serverless API gateway structures, and predictive AI model implementation in logistics SaaS.",
    problem: "Retailers experience high stockouts and waste capital on overstocking because they lack automated analytics and prediction capabilities, relying on manual spreadsheets that fail to capture real-time sales speed and regional seasonality.",
    solution: "Engineered a component-driven TypeScript dashboard with Recharts analytics, connecting to a Node.js microservice. Integrates Google Genkit to run zero-shot prompt analytics on sales datasets, and streams product catalog media through Cloudinary CDN with smart client-side caching.",
    howItWorks: [
      "Warehouse operators sync transaction histories and active stock counts securely through Firebase Auth headers.",
      "The predictive engine triggers daily batch forecasting runs, analyzing historical purchase logs to identify low-stock risks.",
      "For items with declining velocity, the AI service dynamically estimates price elasticity and recommends targeting coupons.",
      "Static assets and catalog images are compressed to WebP and served via Cloudinary's global edge network."
    ],
    features: [
      "Real-time sales velocity monitoring dashboards with Recharts charts",
      "Predictive replenishment alerts powered by Google Genkit AI models",
      "Dynamic discount pricing optimizer recommending campaigns for excess stock",
      "Multi-tenant Firebase Auth and Cloudinary asset management pipelines",
      "Compound indexed lookup filters supporting instant database searches"
    ],
    techStack: {
      frontend: ["NextJS", "TypeScript", "TailwindCSS", "ShadCN UI", "Recharts"],
      backend: ["NodeJS", "Google Genkit", "Firebase Admin SDK", "Cloudinary SDK"],
      infra: ["MongoDB Atlas", "Cloudinary CDN", "Vercel", "GitHub Actions"]
    },
    industryPractices: [
      "Type-safe API contract matching using TypeScript definitions",
      "Compound database indexes to speed up multi-parameter filters",
      "Lazy image loading and CDN-cached static asset distribution",
      "Zero-downtime deployments via serverless edge functions"
    ],
    testing: {
      frontend: "TypeScript compiler checks and Jest testing suite validating charts data structures.",
      backend: "Firebase Local Emulator test scenarios checking token structures and Mongoose write integrity.",
      e2e: "Playwright cross-browser runs verifying active data tables edit/save cycles."
    },
    securityMeasures: [
      "Firebase server-side token verification on API request headers",
      "Restricted MongoDB security groups, IP whitelist ranges, and connection string encapsulation",
      "Encrypted transit channels (HTTPS/TLS) for data synchronizations"
    ],
    performanceOptimizations: [
      "Cursor-based pagination on large transaction list ledger queries",
      "Pre-fetched inventory status counts using Next.js layout route caching",
      "Lazy-loaded charts and media components to optimize initial page loading speeds"
    ],
    observability: {
      errors: "Google Cloud logging console integration tracking runtime anomalies",
      logs: "Structured JSON format logs for serverless endpoints"
    },
    architecture: [
      "Next.js Client Layout",
      "Firebase Client SDK",
      "API Gateway Routes",
      "Genkit NLP Prompts",
      "Gemini Model API",
      "Mongoose Transaction Ledger",
      "MongoDB Storage Engine"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/StockPilot"
    },
    stats: {
      stars: 30,
      users: "150+ active client items",
      performance: "35% faster stock counts"
    },
    responsibilities: [
      "Configured the relational document schemas for sales and inventory tracking.",
      "Wrote the Genkit recommendation query mapping prompts.",
      "Setup dynamic charts using Recharts library components."
    ],
    highlights: [
      "Genkit intelligent promo suggestion logic.",
      "Type-safe client-server models reducing runtime anomalies.",
      "Zero-overhead database lookup latency using optimized compound indices."
    ],
    timeline: "Aug 2025 - Nov 2025",
    status: "Completed"
  },
  {
    id: "url-shortener",
    title: "URL Shortener Engine",
    tagline: "High-performance link redirection engine with visitor click analytics tracking.",
    cover: "/assets/projects/urlshort/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "URL Manager View" }
    ],
    overview: `
      URL Shortener is a lightweight web application that converts long URLs into unique, 6-character shortened link slugs.
      It features an Express.js router that performs fast redirection routing, tracks dynamic click counts, logs user agents, and renders live hit charts using EJS templates.
    `,
    importance: "Demonstrates practical knowledge of routing microservices, link hashing collision algorithms, and basic schema counter collections in MongoDB.",
    problem: "Sharing verbose, tracking-heavy URL strings consumes text boundaries and makes user tracking metrics difficult to record on marketing operations.",
    solution: "Designed an Express routing engine that checks input URLs against strict patterns, runs base-62 hashing, and stores metrics objects using MongoDB indexed keys.",
    howItWorks: [
      "Users input links into the server-side template form.",
      "The server validates URL structure and runs a base-62 collision check.",
      "Saved maps are stored in MongoDB collections.",
      "Visiting the shortened path looks up the original URL, increments the click counter, and issues a 302 redirect."
    ],
    features: [
      "Base-62 url hash slug mappings generation",
      "Dynamic click counters logging user-agent metadata",
      "EJS dashboard rendering aggregated link click-rate lists",
      "URL syntax validation and cleanup helpers"
    ],
    techStack: {
      frontend: ["JavaScript", "HTML5", "EJS Templates", "TailwindCSS"],
      backend: ["NodeJS", "Express", "Crypto Hashing Utility"],
      infra: ["MongoDB Atlas", "Render App Hosting"]
    },
    industryPractices: [
      "URL validation regex checking protocols and domains",
      "302 temporary redirects preventing browser link search ranking locks",
      "No-cache headers on redirection routes to guarantee fresh logs collection"
    ],
    testing: {
      frontend: "Manual UI text-field validator tests checking extreme input strings.",
      backend: "Mocha/Chai tests verifying base-62 hash generators and routing controllers.",
      e2e: "Mock click redirect simulations checking analytics increments."
    },
    securityMeasures: [
      "IP-based rate limits preventing script bots from exhaustively generating mappings",
      "String escaping on EJS templates blocking Cross-Site Scripting (XSS) inputs"
    ],
    performanceOptimizations: [
      "Single-key indexing on slug parameter queries inside MongoDB database models",
      "Minimalist asset loading and server-side rendering to trim page weight"
    ],
    observability: {
      errors: "Express global error catcher middleware logging stack traces",
      logs: "Standard console request logging documenting hits timestamps"
    },
    architecture: [
      "EJS Form Interface",
      "Redirection API Router",
      "Base-62 Slug Hashing",
      "Mongoose Hit Logger",
      "MongoDB Key Collections"
    ],
    links: {
      live: "https://url-shortner-c7ck.onrender.com/",
      repo: "https://github.com/Chahatj23/url-shortner"
    },
    stats: {
      stars: 25,
      users: "500+ generated routes",
      performance: "50ms query redirects"
    },
    responsibilities: [
      "Wrote base-62 compression math helpers in JavaScript.",
      "Programmed Express redirection routes and Mongo counter loops.",
      "Styled and coded EJS template page dashboard structures."
    ],
    highlights: [
      "Decoupled analytics updates to run asynchronously without slowing user redirects.",
      "Fully responsive inputs form scaling perfectly on mobile screens."
    ],
    timeline: "May 2025 - Jul 2025",
    status: "Completed"
  },
  {
    id: "chat-messaging-app",
    title: "Chat Messaging App",
    tagline: "Real-time web messenger incorporating private chat channels and status indicators.",
    cover: "/assets/projects/chat/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "Chat Lobby View" }
    ],
    overview: `
      A real-time messaging application designed to scale socket channels using StreamChat API.
      Built with React and Zustand, it hosts dynamic channels, user activity state lights, and text messages with typing indicators and image attachment capabilities.
    `,
    importance: "Demonstrates practical expertise in state-of-the-art WebSockets connections, third-party messaging SDK hooks, and Zustand store slices.",
    problem: "Hosting and scaling real-time chat sockets on monolithic server threads leads to massive resource locks and message delivery sync drops during peak traffic.",
    solution: "Delegated socket management to StreamChat Cloud. Built a React client driven by Zustand state stores to isolate channel views, typing indicators, and presence updates.",
    howItWorks: [
      "Client UI connects to the StreamChat cloud socket cluster.",
      "Users select channels or trigger new private direct messaging lobbies.",
      "WebSocket payloads dispatch messages and presence heartbeats in real-time.",
      "Zustand stores track active workspace channels to sync visual components."
    ],
    features: [
      "Real-time WebSocket chat messages delivery",
      "Typing indicators and active user presence tracking lights",
      "Public discussion channels and private peer-to-peer messaging rooms",
      "Media attachments uploads via built-in SDK components"
    ],
    techStack: {
      frontend: ["ReactJS", "Zustand", "StreamChat SDK", "TailwindCSS"],
      backend: ["NodeJS", "StreamChat Server Token Token Generator"],
      infra: ["Vercel Hosting", "StreamChat Cloud API"]
    },
    industryPractices: [
      "Optimized React component rendering using strict state memoizations",
      "Securing client tokens by signing user payloads server-side",
      "Skeleton load states while retrieving channel histories"
    ],
    testing: {
      frontend: "React Testing Library validating channel selection and message form displays.",
      backend: "Jest server tests validating JWT socket token signing controllers.",
      e2e: "Mock user connection test scripts checking instant messages reflection."
    },
    securityMeasures: [
      "JWT user token validation verified on socket handshakes",
      "StreamChat permission rules checking client scopes before channel writes"
    ],
    performanceOptimizations: [
      "Message history paginations reducing DOM tree weights in active channels",
      "Zustand state slices decoupling header components from active chat list updates"
    ],
    observability: {
      errors: "StreamChat developer console dashboard warnings tracker",
      logs: "WebSocket network frames logged inside the developer browser panel"
    },
    architecture: [
      "React Messenger Window",
      "Zustand Active Channel Store",
      "StreamChat Client SDK",
      "StreamChat WebSockets Gateway"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/chat-app"
    },
    stats: {
      stars: 15,
      users: "100+ active rooms",
      performance: "10ms message updates"
    },
    responsibilities: [
      "Integrated StreamChat UI component kits with custom Tailwind styling themes.",
      "Programmed the Node.js backend user token generator API.",
      "Designed Zustand global store slices managing visual layouts states."
    ],
    highlights: [
      "Stateless Token signing backend ensuring secure chat socket allocations.",
      "Excellent component-level performance isolating typing state shifts."
    ],
    timeline: "Dec 2024 - Jan 2025",
    status: "Completed"
  },
  {
    id: "home-automation-system",
    title: "Home Automation System using AI and ML",
    tagline: "Smart IoT home lock and control system powered by OpenCV classification on edge.",
    cover: "/assets/projects/iot/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "IoT Hub View" }
    ],
    overview: `
      An IoT-focused smart home system featuring facial recognition security gates and dynamic appliance power controls.
      It combines edge-based OpenCV face detection with a Flask API that coordinates with Firebase Realtime Database to operate physical hardware relays.
    `,
    importance: "Showcases capabilities in edge computer vision, hardware-cloud data synchronization pipelines, and Flask microservices design.",
    problem: "Standard IoT setups rely on manual inputs and lack visual authentication layers, risking slow trigger response cycles during security warnings.",
    solution: "Deployed a local OpenCV classifier checking faces. Connects to Flask endpoints to push notifications and write lock states directly to Firebase database.",
    howItWorks: [
      "Video feeds from lock hardware are processed locally by OpenCV.",
      "Known face classifications trigger database lock values shifts.",
      "Firebase Realtime DB syncs states instantly to active appliance relay boards.",
      "Unknown triggers record capture data and push alerts to client dashboards."
    ],
    features: [
      "OpenCV facial recognition lock classification engines",
      "Real-time visual security alerts and video feeds portal",
      "Instant state updates syncing via Firebase Realtime Database",
      "Dynamic device relay power switches"
    ],
    techStack: {
      frontend: ["Android Java SDK", "XML UI Layouts", "TailwindCSS Web Dashboard"],
      backend: ["Flask API", "Python OpenCV Library", "Face Classifier Framework"],
      infra: ["Firebase Realtime Database", "Back4App Database Storage"]
    },
    industryPractices: [
      "Hardware state caching to handle database offline dropouts",
      "Threaded video capture loops to prevent image frame analysis blockages",
      "Standardised JSON data formats syncing with REST endpoints"
    ],
    testing: {
      frontend: "Android Studio layout renderings and event trigger clicks checks.",
      backend: "Local Python test executions checking classifier detection bounds.",
      e2e: "Hardware-to-Cloud runs checking face matches triggering relay actions."
    },
    securityMeasures: [
      "AES-256 encrypted security databases for user facial matrix signatures",
      "Firebase Database security rules locking data write endpoints to secure device keys"
    ],
    performanceOptimizations: [
      "Resized camera video stream frames to speed up local matrix classifications",
      "Optimized OpenCV lookup cycles using pre-cached facial embeddings indexes"
    ],
    observability: {
      errors: "Flask runtime traceback logs checking hardware connection states",
      logs: "Detailed access logs documentations recording entry timestamps and match names"
    },
    architecture: [
      "Android Client / Web View",
      "OpenCV Face Recognizer",
      "Flask Local Controller",
      "Firebase Realtime DB Sync",
      "Physical Relay Outputs"
    ],
    links: {
      live: "https://github.com/Chahatj23/Home-Automation-System-Android-App",
      repo: "https://github.com/Chahatj23/Home-Automation-System-Android-App"
    },
    stats: {
      stars: 40,
      users: "5 active hardware builds",
      performance: "800ms match response"
    },
    responsibilities: [
      "Designed and coded the Flask local server and camera processing loops.",
      "Integrated Firebase Realtime database syncing logic.",
      "Coded the Android Java management client app."
    ],
    highlights: [
      "Local processing ensures fast locking access check loops even with poor internet.",
      "Reliable pub/sub state mapping using Firebase Realtime Database."
    ],
    timeline: "Feb 2024 - Jul 2024",
    status: "Completed"
  },
  {
    id: "amazon-web-scraper",
    title: "Amazon Web Scraper",
    tagline: "Python data extractor gathering pricing indexes and review metrics.",
    cover: "/assets/projects/scraper/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "Scraper output" }
    ],
    overview: `
      An automated Python scraper configured to aggregate catalog metrics from Amazon.
      Using BeautifulSoup4 and Requests, it crawls product listings, extracts prices, ratings, and stock status indicators, and compiles structured JSON data tables.
    `,
    importance: "Demonstrates familiarity with DOM tree parse models, complex HTTP headers manipulation, and bot detection mitigation strategies.",
    problem: "Gathering catalog price data for market index trackers manually takes hundreds of hours and is prone to human typing errors.",
    solution: "Coded a modular Python script using Requests, BeautifulSoup4, and custom headers. Utilized randomized delays and User-Agent rotates to prevent target scrap blockers.",
    howItWorks: [
      "Scraper script reads targeted ASIN lists.",
      "HTTP Requests pulls DOM raw trees using randomized browser headers.",
      "BeautifulSoup parses target product container nodes.",
      "Cleaned price parameters append to database CSV outputs."
    ],
    features: [
      "Multi-ASIN loop parsing and details scraping",
      "Dynamic User-Agent rotator headers bypassing server blocks",
      "Automated CSV pricing log databases exporter",
      "Trigger alert email notifications on price drop occurrences"
    ],
    techStack: {
      frontend: ["CLI Status Panel", "SMTP Email Alerts"],
      backend: ["Python Scripting", "BeautifulSoup 4", "Requests Library"],
      infra: ["Local Task Scheduler", "CSV/JSON File Exporter"]
    },
    industryPractices: [
      "Parsing specific DOM selectors using ID attributes to save cpu cycles",
      "Strict data sanitizations trimming currency identifiers",
      "Respecting robot.txt delay rules during execution loops"
    ],
    testing: {
      frontend: "Validating text table prints on console output channels.",
      backend: "Mock tests checking DOM selector matching reliability.",
      e2e: "Scheduled cron task runs checking output files durability."
    },
    securityMeasures: [
      "Encrypted email credentials parameters inside environment settings",
      "Input bounds checking preventing directory traversal during file creation"
    ],
    performanceOptimizations: [
      "Buffered write executions saving memory arrays to disk in chunks",
      "Disabled heavy script execution paths on scraped HTML bodies"
    ],
    observability: {
      errors: "Traceback prints documenting parsing dropouts and captcha block alerts",
      logs: "Detailed runtime audit trails tracking processed product items"
    },
    architecture: [
      "Target ASIN Registry",
      "User-Agent Rotation Pool",
      "Requests Document Downloader",
      "BeautifulSoup Parse Node",
      "Local CSV Database"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/Amazon-Web-Scraper"
    },
    stats: {
      stars: 12,
      users: "10+ daily cron tasks",
      performance: "1.2s parse cycle"
    },
    responsibilities: [
      "Programmed the BeautifulSoup HTML parsing node patterns.",
      "Implemented requests rotation delays logic.",
      "Configured the local CSV writer output structures."
    ],
    highlights: [
      "Excellent headers mapping preventing target system crawl restrictions.",
      "Clean price conversions extracting clean float values from currency strings."
    ],
    timeline: "Mar 2024 - May 2024",
    status: "Completed"
  },
  {
    id: "unified-ai-agent",
    title: "Unified AI Lead Agent (Email, Chatbot & Scheduler)",
    tagline: "Omnichannel business qualification pipeline integrating OpenAI NLP with enterprise tools.",
    cover: "/assets/projects/aiagent/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/celular-2.png", alt: "Mobile Chat Bot Preview" }
    ],
    overview: `
      Unified AI Lead Agent is an automated lead generation, qualification, and scheduling pipeline.
      It monitors incoming communications from Instagram, WhatsApp, and email, utilizing OpenAI Function Calling to extract business intent, check lead budgets, and verify technical requirements.
      Qualified prospects are dynamically written to HubSpot and Salesforce CRMs, and booked via Calendly webhooks, with high-priority notifications dispatched instantly via Twilio SMS and Voice.
    `,
    importance: "Demonstrates advanced knowledge of agentic integration workflows, structured function call extractions, decoupled n8n workflow queues, and webhook-driven serverless setups.",
    problem: "Sales development representatives (SDRs) spent up to 40% of their day manually qualifying cold leads and coordinating calendars, resulting in slow reply times and missed pipeline conversions.",
    solution: "Built an asynchronous webhook-driven microservice orchestration layout using n8n and Zapier. Integrates OpenAI GPT model function calling to analyze messaging transcripts, map client fields, and trigger real-time Twilio voice alerts.",
    howItWorks: [
      "Webhook channels (Twilio/ManyChat) intercept incoming client messages and stream them to the n8n orchestrator.",
      "The orchestrator sends the conversation logs to the OpenAI Assistant API using structured function calls.",
      "If the prospect is qualified, the assistant retrieves scheduling slots and calls the Calendly booking engine.",
      "HubSpot and Salesforce databases update instantly, and Twilio dials the sales agent for immediate conversion."
    ],
    features: [
      "Omnichannel support processing WhatsApp, Instagram, and email threads",
      "AI intent analysis and contact data parsing using OpenAI function calling schemas",
      "Real-time CRM contact creation and custom fields synchronization rules",
      "Automated Calendly scheduler synchronization and validation",
      "Twilio voice dispatcher executing hot-lead telephone handoffs"
    ],
    techStack: {
      frontend: ["ReactJS", "TailwindCSS (internal admin panel)"],
      backend: ["NodeJS", "OpenAI Assistant API", "n8n Webhook Gateways", "Zapier"],
      infra: ["Salesforce API", "HubSpot API", "Calendly Webhooks", "Twilio Voice"]
    },
    industryPractices: [
      "Asynchronous webhook queuing to handle peak load spikes",
      "Prompt validation tests and backup context models",
      "API request retry logic with exponential backoffs",
      "Decoupled event logging for auditing conversation histories"
    ],
    testing: {
      frontend: "React render tests validating chat interface components and webhook tracking tables.",
      backend: "Supertest route matching and mock webhook triggers asserting JSON return structures.",
      e2e: "Mock end-to-end chat simulation test pipelines checking multi-channel intent extractions."
    },
    securityMeasures: [
      "HMAC webhook signature validations verifying provider authenticity",
      "Restricted API token scopes, secure credentials vaults, and environment variable masking",
      "Sanitizing input parameters prior to database writes in HubSpot/Salesforce"
    ],
    performanceOptimizations: [
      "Context compression to shrink long thread payload tokens and minimize API billing fees",
      "Parallelized database writing loops reducing record synchronization latency to under 3 seconds",
      "Caching timezone conversion logic in the scheduler to speed up calendar rendering"
    ],
    observability: {
      errors: "Slack error-reporting channel alerts tracking failed API invocations",
      logs: "n8n node execution history and centralized application logs"
    },
    architecture: [
      "Webhook Web Entry (Twilio/ManyChat)",
      "Express API Payload Parser",
      "n8n Decoupled Queue Orchestration",
      "OpenAI Assist Function Dispatcher",
      "HubSpot / Salesforce REST Handlers",
      "Twilio Voice Telephony Callout"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/Unified-AI-Lead-Agent"
    },
    stats: {
      stars: 55,
      users: "100K+ runs executed",
      performance: "100% automated leads"
    },
    responsibilities: [
      "Architected n8n workflow branches and exception handling steps.",
      "Wrote JavaScript parsing functions for customer response arrays.",
      "Programmed the CRM integration hooks and fields mapping."
    ],
    highlights: [
      "Automated lead response times down from 4 hours to 90 seconds.",
      "95% accuracy in intent extraction through few-shot prompt definitions."
    ],
    timeline: "Oct 2024 - Feb 2025",
    status: "Active"
  },
  {
    id: "personalized-career-mentor-ai",
    title: "Personalized Career Mentor AI",
    tagline: "Career guidance platform grounding LLM analysis with search and video APIs.",
    cover: "/assets/projects/mentor/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/celular-2.png", alt: "Mentor Response View" }
    ],
    overview: `
      An AI career mentoring tool submitted to Google x Kaggle.
      Built with Python and LangChain, it orchestrates Gemini models with grounding to dynamically output customized career roadmaps and relevant YouTube educational courses.
    `,
    importance: "Demonstrates familiarity with LLM grounding frameworks, complex chain pipelines, and prompt injection mitigation patterns.",
    problem: "Standard AI chatbot advice is frequently outdated, recommending stale certification paths and dead resource links.",
    solution: "Coded a LangChain workflow that runs user inputs, retrieves validated web resources using Google Search API, and queries YouTube video metadata for course mappings.",
    howItWorks: [
      "User details interests and targets in the input prompt.",
      "LangChain parses parameters and formats a structured grounding prompt.",
      "Google Search/YouTube APIs fetch fresh instructional context logs.",
      "Gemini compiles the grounded career roadmap in markdown formats."
    ],
    features: [
      "Grounded roadmap generations backed by Google Search API",
      "Dynamic course link injections powered by YouTube Metadata API",
      "LangChain prompt template chains managing parameters context",
      "Interactive Kaggle Notebook execution environment"
    ],
    techStack: {
      frontend: ["Kaggle Notebook UI", "Markdown Output Formatters"],
      backend: ["Python", "LangChain Framework", "Gemini API Client SDK"],
      infra: ["Google Search REST Wrapper", "YouTube Data API Gateway"]
    },
    industryPractices: [
      "Grounding prompt schemas preventing LLM hallucinations",
      "Parsing APIs returning structured JSON payloads to save memory",
      "Rate-limit management tracking API request cycles"
    ],
    testing: {
      frontend: "Visual review checking markdown formatting syntax outputs.",
      backend: "Unit tests verifying search retrieval result parsing nodes.",
      e2e: "Mock profiles processed checked for accuracy of link recommendations."
    },
    securityMeasures: [
      "API keys masked inside kaggle credentials environments vaults",
      "Strict parameter controls preventing user prompt injection tricks"
    ],
    performanceOptimizations: [
      "Caching search results to avoid redundant API request charges",
      "Streamed output processing showing markdown blocks progressively"
    ],
    observability: {
      errors: "LangChain debugging outputs tracking pipeline node performance",
      logs: "Detailed prompt trackers recording token costs and match speeds"
    },
    architecture: [
      "User Target Form",
      "LangChain Prompt Assembler",
      "Gemini AI Engine",
      "Search Grounding Hooks",
      "YouTube Video Selector",
      "Markdown Output Renderer"
    ],
    links: {
      live: "https://www.kaggle.com/code/chahatj23/personalized-ai-mentor",
      repo: "https://www.kaggle.com/code/chahatj23/personalized-ai-mentor"
    },
    stats: {
      stars: 60,
      users: "200+ Kaggle notebook reads",
      performance: "1.5s roadmap processing"
    },
    responsibilities: [
      "Programmed the LangChain sequence triggers.",
      "Wrote search payload parsing scripts in Python.",
      "Designed prompt grounding templates."
    ],
    highlights: [
      "Grounding guarantees real-world links and verified study paths.",
      "Saves API fees through smart search caching."
    ],
    timeline: "Jan 2025 - Feb 2025",
    status: "Completed"
  },
  {
    id: "musify-music-player-app",
    title: "Musify – Music Player App",
    tagline: "Android music streaming app featuring remote database syncing and offline SQLite storage.",
    cover: "/assets/projects/musify/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/celular-2.png", alt: "Player Interface screen" }
    ],
    overview: `
      Musify is a native Android music player app.
      Using ExoPlayer for audio rendering, it synchronizes catalogs via Firebase Realtime Database and stores user track lists inside local SQLite tables for offline playback.
    `,
    importance: "Demonstrates competencies in Android lifecycle management, local file serialization, and remote catalog sync configurations.",
    problem: "Streaming apps fail completely when internet signals drop, resulting in catalog browse locks and playback interruptions.",
    solution: "Coded an Android Java application integrated with Firebase Realtime Database. Implemented SQLite databases locally to sync track indices and support offline ExoPlayer fallback loops.",
    howItWorks: [
      "App connects to Firebase to list online catalogue indexes.",
      "Clicking downloads remote audio files into cache folders.",
      "ExoPlayer plays cached data stream tracks.",
      "During offline states, ExoPlayer falls back to play paths listed in SQLite."
    ],
    features: [
      "ExoPlayer audio streaming playback controllers",
      "Offline playback database sync via SQLite",
      "Firebase database online catalogue indexing",
      "Dynamic user authentication profiles via Firebase Auth"
    ],
    techStack: {
      frontend: ["Java (Android SDK)", "XML ConstraintLayout", "ExoPlayer UI Components"],
      backend: ["Firebase Realtime Database", "Firebase Auth SDK"],
      infra: ["SQLite Local Storage Database", "Firebase Cloud Storage"]
    },
    industryPractices: [
      "Managing audio play threads outside main UI operations to prevent freezing",
      "Bulk database queries execution checking local cache status",
      "Responsive XML layout constraint chains matching screen sizes"
    ],
    testing: {
      frontend: "Android Studio layout renderings and screen animations test checks.",
      backend: "Firebase connection tests asserting login credentials checks.",
      e2e: "Device emulator runs testing offline transition audio play."
    },
    securityMeasures: [
      "Encrypted user credentials within Firebase Client authentication flows",
      "Parameterized SQLite query binds preventing SQL injection vulnerabilities"
    ],
    performanceOptimizations: [
      "Dynamic image downscaling to prevent album art buffers from causing OutOfMemory crashes",
      "Asynchronous file buffering pipelines to reduce audio start delays"
    ],
    observability: {
      errors: "Android Logcat exception reports tracking media decoding issues",
      logs: "Standard Android log events tracing active playback status events"
    },
    architecture: [
      "Android Activity View",
      "ExoPlayer Controller",
      "SQLite Offline Indexer",
      "Firebase Media Downloader"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/Musify"
    },
    stats: {
      stars: 22,
      users: "50+ local active installations",
      performance: "100ms offline load times"
    },
    responsibilities: [
      "Programmed SQLite tracking models and schema structures.",
      "Integrated ExoPlayer playback listener classes.",
      "Coded the Android XML interfaces and responsive layouts."
    ],
    highlights: [
      "Seamless transitions to offline audio play without thread locking.",
      "Reliable SQLite databases index matching online catalogue parameters."
    ],
    timeline: "Jun 2024 - Aug 2024",
    status: "Completed"
  },
  {
    id: "interest-calculator-app",
    title: "Interest Calculator App",
    tagline: "Android interest calculations tool running complex compound calendar schedules.",
    cover: "/assets/projects/calc/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/celular-2.png", alt: "Calculator Form view" }
    ],
    overview: `
      A utility Android app programmed in Kotlin.
      It executes simple and compound interest calculations, factoring in custom date durations and compounding schedules.
    `,
    importance: "Demonstrates capability in Kotlin coding, Android UI development, and complex algorithm calendar operations.",
    problem: "Standard calculators do not support custom date-interval logic (like 'Till Today' ranges) or custom compounding periods.",
    solution: "Coded a native Kotlin utility app. Implemented LocalDate date-duration handlers and custom mathematical loops to output compounding calculations in real-time.",
    howItWorks: [
      "Users inputs principal, rate, start date, and end date fields.",
      "Kotlin date parser calculates elapsed days/months details.",
      "Mathematical functions calculate the simple/compound interest values.",
      "Result displays instantly on the text layout."
    ],
    features: [
      "Simple & Compound interest arithmetic engines",
      "LocalDate dynamic date-interval calculator ('Till Today')",
      "Clean native Android ConstraintLayout views",
      "Compounding schedules filters (Monthly, Quarterly, Annually)"
    ],
    techStack: {
      frontend: ["Kotlin (Android SDK)", "XML ConstraintLayout"],
      backend: ["Kotlin Math Engine"],
      infra: ["Android SDK compiler tools"]
    },
    industryPractices: [
      "Localized date format parsing matching device regional settings",
      "Resource isolation separation within Android activity components",
      "Single-responsibility code files for layout classes"
    ],
    testing: {
      frontend: "Android Studio emulator layout bounds validation testing.",
      backend: "JUnit tests validating interest calculations mathematical output accuracy.",
      e2e: "Manual field entries testing inputs boundary checks."
    },
    securityMeasures: [
      "Form level validator checks blocking division-by-zero or negative inputs"
    ],
    performanceOptimizations: [
      "Pre-allocated calculation dates parameters to avoid garbage collection loops"
    ],
    observability: {
      errors: "Kotlin runtime Exception reports catching parsing exceptions",
      logs: "Standard console logs tracing computed calculations count"
    },
    architecture: [
      "Kotlin Form Component",
      "LocalDate Range Analyzer",
      "Interest Mathematical Engine",
      "UI Output Display"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/Interest-Calculator"
    },
    stats: {
      stars: 14,
      users: "300+ local downloads",
      performance: "Instant calculations output"
    },
    responsibilities: [
      "Coded the Kotlin mathematical calculation formulas.",
      "Integrated LocalDate calendar duration calculation classes.",
      "Designed XML constraint layouts interfaces."
    ],
    highlights: [
      "Accurate date mathematics accounting for leap years.",
      "Clean UI scaling perfectly across diverse screen ratios."
    ],
    timeline: "May 2024 - Jun 2024",
    status: "Completed"
  },
  {
    id: "student-record-management-system",
    title: "Student Record Management System",
    tagline: "CLI-based OOP student record manager utilizing fstream database serialization.",
    cover: "/assets/projects/student/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/notebook.png", alt: "CLI Interface print" }
    ],
    overview: `
      A lightweight C++ CLI tool that executes CRUD operations on student record collections.
      It features object-oriented design patterns and utilizes fstream file streams to persist record datasets.
    `,
    importance: "Demonstrates mastery of core computer science fundamentals, file system serialization, memory safety, and C++ OOP design structures.",
    problem: "Storing student catalog records in active memory loses data during console shutdowns and leads to memory leak traps.",
    solution: "Coded a C++ command-line application using classes and structs. Implemented file stream (fstream) read/write actions to persist records as formatted text database files.",
    howItWorks: [
      "Admin launches the C++ CLI console utility.",
      "Console offers options: Create, Read, Update, or Delete student records.",
      "Inputs instantiate Student objects verified by field rules.",
      "Record is appended to text file databases on disk."
    ],
    features: [
      "Complete CLI console CRUD navigation panel",
      "File-based fstream database serialization system",
      "Object-oriented class structures mapping data parameters",
      "Key-based database record search filtering options"
    ],
    techStack: {
      frontend: ["C++ Console CLI Output"],
      backend: ["C++ Standard OOP Library", "Fstream File System APIs"],
      infra: ["Local Disk Files Storage Database"]
    },
    industryPractices: [
      "Passing objects by reference to avoid redundant stack copy operations",
      "Opening file streams in binary modes to prevent line-ending conversion issues",
      "Applying private member caps to enforce OOP encapsulation principles"
    ],
    testing: {
      frontend: "Visual inspection testing visual text alignments.",
      backend: "C++ compiler checks checking pointer ranges safety bounds.",
      e2e: "Mock input streaming files verifying record writing loops."
    },
    securityMeasures: [
      "Buffer overflow protection checks validating input length limits"
    ],
    performanceOptimizations: [
      "Cached file streams buffering memory arrays to reduce direct disk IO hits",
      "Single pass file parsing during record search loops"
    ],
    observability: {
      errors: "Standard stream state checks warning when target database files are missing",
      logs: "Runtime print logs tracing operations executed"
    },
    architecture: [
      "CLI Console Selector",
      "C++ Student Object Model",
      "Fstream File Manager",
      "Local Text Database"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/Student-Record-Management-System"
    },
    stats: {
      stars: 10,
      users: "100+ records tracked",
      performance: "0.1ms search index lookups"
    },
    responsibilities: [
      "Coded the C++ student class structures and models.",
      "Programmed the fstream data write/read routines.",
      "Configured compilation settings and manual text tests."
    ],
    highlights: [
      "Clean encapsulation utilizing header and source files isolation patterns.",
      "Durable database persistence writing directly to local text files."
    ],
    timeline: "Jan 2024 - Feb 2024",
    status: "Completed"
  },
  {
    id: "salesforce-hubspot-crm-enhancements",
    title: "Salesforce & HubSpot CRM Enterprise Solutions",
    tagline: "LWC dashboards and real-time CRM data sync modules built for sales optimization.",
    cover: "/assets/projects/crm/cover.jpg",
    media: [
      { type: "image", src: "/assets/images/vidro.png", alt: "LWC Component Screen" }
    ],
    overview: `
      This CRM Enterprise Solutions suite optimizes lead scoring pipelines and synchronizes operations between Salesforce Sales Cloud and HubSpot CRM.
      It features complex Apex trigger frameworks, pub/sub Platform Events, and custom Lightning Web Components (LWC).
      The system executes real-time data synchronization and runs background batch calculations for customer health scores without hitting API limits.
    `,
    importance: "Demonstrates deep knowledge of CRM database engines, bulkified Apex execution models, Salesforce governor limits, and event-driven integration flows.",
    problem: "Sales pipelines suffered from stale contact data and siloed metrics, leading to misaligned deal scoring between HubSpot marketing and Salesforce sales systems.",
    solution: "Programmed custom Apex triggers, Queueable classes, and LWC dashboards. Uses Platform Events to map records asynchronously, and Apex Schedulers to run customer scoring algorithms.",
    howItWorks: [
      "Sales reps modify contact details or deal values in Salesforce.",
      "Apex triggers capture changes and fire outbound REST HTTP payloads or Platform Events.",
      "HubSpot synchronization engines process payload inputs and sync matching contacts instantly.",
      "Apex Schedule jobs calculate lead health indicators nightly and update metrics tables."
    ],
    features: [
      "Dynamic Customer Health Score calculations powered by Apex schedulers",
      "High-performance LWC dashboards with custom database query selectors",
      "Asynchronous pub/sub Platform Event integrations decoupling sync engines",
      "Two-way conflict resolution engine matching record modification timestamps"
    ],
    techStack: {
      frontend: ["LWC (Salesforce)", "Salesforce Flows", "HTML5/CSS3"],
      backend: ["Apex Programming", "REST/SOAP API", "Platform Events", "SOQL/SOSL Queries"],
      infra: ["Sales Cloud", "Service Cloud", "HubSpot Webhooks", "Salesforce Sandbox"]
    },
    industryPractices: [
      "Salesforce Apex Governor Limits optimization (bulkified loops)",
      "Strict data sharing rules and permission set configurations",
      "Two-way synchronization conflict resolution rules",
      "Metadata deployment checks utilizing Salesforce CLI"
    ],
    testing: {
      frontend: "Jest unit tests for Salesforce Lightning web components.",
      backend: "Apex Test Classes with 92% coverage validation targets checking bulk updates and exception triggers.",
      e2e: "Mock Sandbox synchronization E2E tests validating outbound REST callbacks."
    },
    securityMeasures: [
      "Strict enforcement of Salesforce 'with sharing' context keywords to prevent record exposure",
      "Secure Named Credentials and custom headers encrypting outbound API endpoints",
      "Field-Level Security checks integrated in SOQL select queries"
    ],
    performanceOptimizations: [
      "Bulkified Apex trigger loops preventing governor limits violations",
      "Optimized SOQL database queries avoiding non-indexed wildcard filters",
      "Delegated long-running callouts to Queueable and Future context threads"
    ],
    observability: {
      errors: "Salesforce Apex Error Log emails and custom debug tracking logs",
      logs: "Developer console debug runs and transaction trace streams"
    },
    architecture: [
      "Lightning Web Components",
      "Apex Database Triggers",
      "Platform Events Queue",
      "Queueable Asynchronous Apex",
      "HubSpot Webhook Receivers",
      "Bi-directional Data Sync Sync Engine"
    ],
    links: {
      live: "#",
      repo: "https://github.com/Chahatj23/CRM-Integrations"
    },
    stats: {
      stars: 18,
      users: "20+ production setups",
      performance: "95% manual task cut"
    },
    responsibilities: [
      "Programmed Apex bulk triggers and queueable integration classes.",
      "Designed LWC dashboards representing account health statistics.",
      "Configured security permissions, profiles, and sharing settings."
    ],
    highlights: [
      "Zero data overlap through strict ID resolution rules.",
      "Maintained 92% test coverage across Salesforce Apex business code."
    ],
    timeline: "Nov 2024 - Present",
    status: "Active"
  },
  {
    id: "safari-immersive-shopify-experience",
    title: "SafariCraft — Premium Immersive Shopify Experience",
    tagline: "A custom Shopify store featuring shoppable hotspots, dynamic bento grids, and custom admin apps with Sidekick AI tool integrations.",
    cover: "/assets/projects/safaricraft/cover.jpg",
    media: [
      { type: "image", src: "/assets/projects/safaricraft/home.png", alt: "SafariCraft Home Page" },
      { type: "image", src: "/assets/projects/safaricraft/radio.png", alt: "Safari Radio Custom Section" },
      { type: "image", src: "/assets/projects/safaricraft/hotspots.png", alt: "Interactive Product Hotspots" }
    ],
    overview: `
      SafariCraft is an immersive, safari-themed premium Shopify storefront built for a high-end lifestyle brand. It features a custom Liquid theme layered with advanced front-end components like custom shoppable image hotspots, asymmetrical bento grids, and dynamic audio integration with Spotify.
      To support operations, we built a local Shopify App ecosystem featuring a Preact admin dashboard database mapping native Metaobjects, dynamic checkout UI discount extensions, and custom tools exposing the app's functionality directly to Shopify Sidekick AI.
    `,
    importance: "Demonstrates the ability to bridge premium frontend Liquid codecraft with complex, fullstack Shopify App extensions (Admin Dashboard, Checkout Extensions, and Shopify Sidekick AI tools).",
    problem: "Providing a highly unique, safari-branded shopping experience with high-fidelity visual elements (such as split-media comparison sliders and image hotspots) while maintaining page performance and offering merchants simple ways to manage FAQs and custom checkout discount campaigns.",
    solution: "Designed custom Liquid sections (Safari Radio, Bento Grid, Comparison Slider, and Shoppable Hotspots) using modular configurations. Developed a localized Preact Shopify App linking to Shopify's GraphQL Admin API to curate FAQs stored as Metaobjects, registered Sidekick AI tools for conversational search, and built Preact checkout widgets for automated cart coupon applications.",
    howItWorks: [
      "Merchants manage FAQs using the custom Preact dashboard, which syncs directly with Shopify Metaobjects via Admin GraphQL mutations.",
      "The AI-powered Sidekick assistant utilizes registered tool handlers (list_faqs, get_faq) to answer merchant content queries in real-time.",
      "Customers browse the visual storefront, experiencing watercolor sketch layouts, responsive bento grids, and visual hotspot overlays.",
      "At checkout, the custom extension fetches active discount codes from shop/customer metafields, rendering eligible promotions for one-click application."
    ],
    features: [
      "Safari Radio Player: Immersive audio streaming section with dynamic Spotify link parsing and floating watercolor vector graphics.",
      "Interactive Product Hotspots: Shoppable image map overlay triggering responsive product quick-add dialog overlays on hover/tap.",
      "Bento Grid Layout: Asymmetrical CSS Grid masonry collections engine adjusting template areas dynamically (up to 12 items).",
      "Comparison Slider: Interactive split-pane before/after visual adjustments using input range slider clip-paths.",
      "FAQ Metaobjects Manager: Integrated React/Preact Admin dashboard for managing FAQ metaobject lifecycle.",
      "Shopify Sidekick AI Tools: Exposes list and detail FAQ resolvers to Shopify's conversational AI assistant.",
      "Checkout UI Extensions: Preact checkout widget rendering contextual promotions based on metafield logic for instant cart deductions."
    ],
    techStack: {
      frontend: ["Liquid", "Preact", "JavaScript (ES Modules)", "CSS Grid", "TailwindCSS", "Framer Motion"],
      backend: ["NodeJS", "Shopify App Bridge", "GraphQL Admin API", "Storefront API"],
      infra: ["Shopify Plus", "Shopify CLI", "Metaobjects Storage", "Git / GitHub"]
    },
    industryPractices: [
      "Clean semantic Liquid layout patterns",
      "Dynamic browser LocalStorage persistent states",
      "Shopify App Bridge context-aware admin components",
      "Modular responsive image aspect-ratio resizing"
    ],
    testing: {
      frontend: "Manual verification of responsive image hotspot layouts, slider dragging, and audio transitions.",
      backend: "GraphQL query validations and mock metafield evaluations.",
      e2e: "Shopify CLI dev tunnel testing of admin routing and checkout extension rendering."
    },
    securityMeasures: [
      "Shopify session token authentication mapping app scopes",
      "Restricted Storefront API schema queries for checkout components",
      "Sanitized inputs on customer popup and email fields"
    ],
    performanceOptimizations: [
      "Lazy-loaded iframe blocks for Spotify media integration",
      "CSS clip-path layouts for before/after comparison sliders to minimize repaint loops",
      "Preloaded assets and compressed WebP watercolor overlays to maximize Lighthouse scores"
    ],
    observability: {
      errors: "Shopify App Dashboard error reports",
      logs: "Shopify CLI tunnel logging outputs"
    },
    architecture: [
      "Shopify Online Store 2.0 Theme",
      "Liquid Section Rendering Engine",
      "Shopify Admin GraphQL API",
      "Shopify Metaobjects Schema",
      "Preact App Extension Router",
      "Shopify Checkout Extension Host"
    ],
    links: {
      live: "https://www.safari-lifestyle-demo.com",
      repo: "https://github.com/Chahatj23/safari-immersive-shopify"
    },
    stats: {
      stars: "80% Done",
      users: "Active Development",
      performance: "Zero CLS"
    },
    responsibilities: [
      "Built custom Liquid sections (Safari Radio, Bento Grid, and Shoppable Image Hotspots).",
      "Created the Preact Shopify Admin single page application for FAQ Metaobjects CRUD.",
      "Configured the Shopify App Tools sidekick resolvers with JSON schema validations.",
      "Implemented the Preact checkout UI discount promotion manager."
    ],
    highlights: [
      "Immersive jungle-themed Liquid section blocks with watercolor illustration offsets.",
      "Custom Shopify Sidekick AI tool registrations for conversational admin integrations.",
      "Dynamic Metaobject storage interface with custom GraphQL schema handlers."
    ],
    timeline: "May 2026 - Present",
    status: "Active"
  }
];

export default projects;
